import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subscription, merge, timer } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  AdminAwsAccount,
  AdminAwsEvent,
  AdminAwsSummary,
  AdminAzureEvent,
  AdminAzureSubscription,
  AdminAzureSummary,
  AdminGcpEvent,
  AdminGcpProject,
  AdminGcpSummary,
  AdminMonitoredService,
  AdminMonitoringFinding,
  AdminMonitoringServiceState,
  AdminService,
  AdminTeamMember,
  OrgContext,
  OrgIntegration,
  OrgSubscriptionStatus,
  OrgSubscriptionTier,
  PatchOrgSubscriptionDto,
  PatchedOrgSubscription,
  humanizeServiceType,
  serviceTypeBadgeClass,
  serviceTypeIcon,
  isGlobalServiceType,
  providerForServiceType,
  providerMeta,
} from '../../core/services/admin.service';
import { OrgRefreshService } from '../../core/services/org-refresh.service';

// AdminOrgMonitoring is imported but used via the service response type alias below
import type { AdminOrgMonitoring } from '../../core/services/admin.service';

interface EditForm {
  tier: string;
  status: string;
  nextRenewalDate: string;
}

@Component({
  selector: 'app-org-context-sidebar',
  templateUrl: './org-context-sidebar.component.html',
  host: { class: 'block h-full' },
})
export class OrgContextSidebarComponent implements OnDestroy {

  @Output() closed = new EventEmitter<void>();
  @Output() subscriptionSaved = new EventEmitter<PatchedOrgSubscription>();

  context: OrgContext | null = null;
  loading = false;
  error: string | null = null;

  // True during a silent background context refresh (content stays visible)
  contextRefreshing = false;

  editMode = false;
  editForm: EditForm = { tier: '', status: '', nextRenewalDate: '' };
  saving = false;
  saveError: string | null = null;

  activeTab: 'overview' | 'team' | 'services' | 'monitoring' = 'overview';

  // ── Monitoring tab state ──────────────────────────────────────────────────
  monitoringContext: AdminOrgMonitoring | null = null;
  monitoringFindings: AdminMonitoringFinding[] = [];
  monitoringLoading  = false;
  monitoringError:   string | null = null;
  findingsLoading    = false;
  findingsError:     string | null = null;
  findingSeverityFilter = '';
  findingStateFilter    = '';

  private monitoringSub?: Subscription;
  private findingsSub?:   Subscription;

  // ── AWS account actions (support-initiated) ────────────────────────────────
  awsActionAccountId: string | null = null;   // account with an action in flight
  awsActionError:  string | null = null;
  awsActionResult: string | null = null;      // transient success line
  awsConfirmDisconnect: AdminAwsAccount | null = null;
  awsEditRegionsAccountId: string | null = null;
  awsAcctDefaultRegion = 'us-east-1';
  awsAcctEnabledRegions: string[] = ['us-east-1'];
  awsAcctRegionsError: string | null = null;

  // AWS lifecycle audit events (last N days)
  awsEvents: AdminAwsEvent[] = [];
  awsEventsLoading = false;

  // ── Azure subscription actions (support-initiated) — parallel to AWS ──────────
  azureActionSubId: string | null = null;   // subscription with an action in flight
  azureActionError:  string | null = null;
  azureActionResult: string | null = null;
  azureConfirmDisconnect: AdminAzureSubscription | null = null;
  azureEvents: AdminAzureEvent[] = [];
  azureEventsLoading = false;

  // ── GCP project actions (support-initiated) — parallel to AWS/Azure ───────────
  gcpActionProjectId: string | null = null;   // project with an action in flight
  gcpActionError:  string | null = null;
  gcpActionResult: string | null = null;
  gcpConfirmDisconnect: AdminGcpProject | null = null;
  gcpEvents: AdminGcpEvent[] = [];
  gcpEventsLoading = false;

  readonly SUPPORTED_REGIONS = [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2', 'ca-central-1',
    'eu-west-1', 'eu-west-2', 'eu-west-3', 'eu-central-1', 'eu-north-1',
    'ap-south-1', 'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1',
    'ap-northeast-2', 'ap-northeast-3', 'sa-east-1',
  ];

  readonly tierOptions: OrgSubscriptionTier[] = ['FREE', 'PRO', 'ENTERPRISE'];
  readonly statusOptions: OrgSubscriptionStatus[] = ['ACTIVE', 'PAST_DUE', 'CANCELED'];
  readonly SKELETON_ITEMS = Array(3).fill(0);

  private _orgId: string | null = null;
  private fetchSub?: Subscription;
  private detailRefreshSub?: Subscription;

  constructor(
    private readonly adminService: AdminService,
    private readonly refreshService: OrgRefreshService,
  ) {}

  @Input()
  set orgId(value: string | null) {
    if (value === this._orgId) return;
    this._orgId = value;
    this.editMode = false;
    this.saveError = null;
    this.activeTab = 'overview';
    // Reset monitoring state on org change
    this.monitoringContext    = null;
    this.monitoringFindings   = [];
    this.monitoringLoading    = false;
    this.monitoringError      = null;
    this.findingsLoading      = false;
    this.findingsError        = null;
    this.findingSeverityFilter = '';
    this.findingStateFilter    = '';
    this.monitoringSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
    this.contextRefreshing = false;

    // Cancel previous detail refresh subscription before starting a new one
    this.detailRefreshSub?.unsubscribe();
    this.detailRefreshSub = undefined;

    if (value) {
      this.fetchContext(value);

      // While this org is open: refresh on global signals AND on a
      // detail-specific 45 s interval so monitoring changes surface promptly.
      this.detailRefreshSub = merge(
        this.refreshService.refresh$,
        timer(45_000, 45_000).pipe(
          filter(() => document.visibilityState === 'visible'),
        ),
      ).subscribe(() => this.silentRefreshContext());
    } else {
      this.context = null;
      this.loading = false;
      this.error = null;
    }
  }

  get orgId(): string | null {
    return this._orgId;
  }

  ngOnDestroy(): void {
    this.fetchSub?.unsubscribe();
    this.detailRefreshSub?.unsubscribe();
    this.monitoringSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  private fetchContext(orgId: string): void {
    this.fetchSub?.unsubscribe();
    this.loading = true;
    this.error = null;
    this.context = null;
    this.fetchSub = this.adminService.getOrgContext(orgId).subscribe({
      next: ctx => {
        this.context = ctx;
        this.loading = false;
        // Load the AWS + Azure + GCP lifecycle audit trails if any is connected.
        if (ctx.awsSummary?.connected) this.loadAwsEvents(orgId);
        if (ctx.azureSummary?.connected) this.loadAzureEvents(orgId);
        if (ctx.gcpSummary?.connected) this.loadGcpEvents(orgId);
      },
      error: () => {
        this.error = 'Failed to load organization context.';
        this.loading = false;
      },
    });
  }

  // Silent background refresh — keeps existing context visible while refetching.
  // Skipped when: no orgId set, initial load in progress, or edit form is open
  // (to avoid silently overwriting form values the admin is currently editing).
  private silentRefreshContext(): void {
    const orgId = this._orgId;
    if (!orgId || this.loading || this.editMode) return;
    this.contextRefreshing = true;
    this.adminService.getOrgContext(orgId).subscribe({
      next: ctx => {
        this.context = ctx;
        this.contextRefreshing = false;
        // If monitoring tab is already loaded, refresh it in the background too
        if (this.activeTab === 'monitoring' && this.monitoringContext) {
          this.silentRefreshMonitoring(orgId);
        }
      },
      error: () => {
        // Keep existing context — do not clear or show an error banner
        this.contextRefreshing = false;
      },
    });
  }

  private silentRefreshMonitoring(orgId: string): void {
    this.adminService.getOrgMonitoring(orgId).subscribe({
      next: data => { this.monitoringContext = data; },
      error: () => { /* silent */ },
    });
    this.adminService.getOrgMonitoringFindings(orgId, {
      limit:    50,
      severity: this.findingSeverityFilter || undefined,
      state:    this.findingStateFilter    || undefined,
    }).subscribe({
      next: res => { this.monitoringFindings = res.findings; },
      error: () => { /* silent */ },
    });
  }

  openEdit(): void {
    if (!this.context) return;
    const org = this.context.organization;
    this.editForm = {
      tier:            org.subscriptionTier,
      status:          org.subscriptionStatus,
      nextRenewalDate: org.nextRenewalDate ? org.nextRenewalDate.substring(0, 10) : '',
    };
    this.saveError = null;
    this.editMode = true;
  }

  cancelEdit(): void {
    this.editMode = false;
    this.saveError = null;
  }

  saveEdit(): void {
    if (!this.context || !this._orgId || this.saving) return;
    this.saving = true;
    this.saveError = null;

    const dto: PatchOrgSubscriptionDto = {
      tier:            this.editForm.tier   || undefined,
      status:          this.editForm.status || undefined,
      nextRenewalDate: this.editForm.nextRenewalDate || null,
    };

    this.adminService.updateOrgSubscription(this._orgId, dto).subscribe({
      next: updated => {
        if (this.context) {
          this.context = {
            ...this.context,
            organization: {
              ...this.context.organization,
              subscriptionTier:   updated.tier.toUpperCase(),
              subscriptionStatus: updated.status.toUpperCase(),
              nextRenewalDate:    updated.nextRenewalDate,
            },
          };
        }
        this.saving = false;
        this.editMode = false;
        this.subscriptionSaved.emit(updated);
        // Trigger a server-confirmed background refresh for both list and detail
        this.refreshService.trigger();
      },
      error: () => {
        this.saveError = 'Save failed. Please try again.';
        this.saving = false;
      },
    });
  }

  refresh(): void {
    if (this._orgId) this.fetchContext(this._orgId);
  }

  close(): void {
    this.closed.emit();
  }

  // ── Badge / format helpers ────────────────────────────────────────────────────

  tierBadgeClass(tier: string): string {
    const map: Record<string, string> = {
      FREE:       'bg-blue-100 text-blue-700',
      PRO:        'bg-violet-100 text-violet-700',
      ENTERPRISE: 'bg-amber-100 text-amber-700',
    };
    return map[tier?.toUpperCase()] ?? 'bg-zinc-100 text-zinc-600';
  }

  statusBadgeClass(status: string): string {
    const map: Record<string, string> = {
      ACTIVE:   'bg-emerald-100 text-emerald-700',
      PAST_DUE: 'bg-red-100 text-red-700',
      CANCELED: 'bg-zinc-100 text-zinc-500',
    };
    return map[status?.toUpperCase()] ?? 'bg-zinc-100 text-zinc-500';
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      ACTIVE:   'Active',
      PAST_DUE: 'Past Due',
      CANCELED: 'Canceled',
    };
    return map[status?.toUpperCase()] ?? status;
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch {
      return '—';
    }
  }

  tierLimit(tier: string): number {
    const limits: Record<string, number> = { FREE: 1, PRO: 10, ENTERPRISE: 100 };
    return limits[tier?.toUpperCase()] ?? 0;
  }

  // ── Integration helpers ───────────────────────────────────────────────────────

  integrationLabel(provider: string): string {
    return ({ github: 'GitHub', jira: 'Jira', aws: 'AWS', azure: 'Azure', slack: 'Slack', teams: 'Teams' } as Record<string, string>)[provider] ?? provider;
  }

  integrationIcon(provider: string): string {
    return ({ github: 'github', jira: 'list-todo', aws: 'cloud', azure: 'cloud', slack: 'hash', teams: 'message-square' } as Record<string, string>)[provider] ?? 'zap';
  }

  integrationDotClass(status: string): string {
    if (status === 'connected') return 'bg-emerald-500';
    if (status === 'pending')   return 'bg-amber-400';
    return 'bg-zinc-300';
  }

  integrationStatusClass(status: string): string {
    if (status === 'connected') return 'text-emerald-600';
    if (status === 'pending')   return 'text-amber-600';
    return 'text-zinc-400';
  }

  integrationStatusLabel(status: string): string {
    if (status === 'connected') return 'Connected';
    if (status === 'pending')   return 'Pending';
    return 'Disconnected';
  }

  integrationMeta(integration: OrgIntegration): string | null {
    if (!integration.metadata) return null;
    if (integration.provider === 'github') return integration.metadata['login'] || null;
    if (integration.provider === 'jira') {
      const id = integration.metadata['cloudId'];
      return id ? `${id.substring(0, 8)}…` : null;
    }
    if (integration.provider === 'aws') {
      // Multi-account: summarise account count instead of implying a single region.
      const n = Number(integration.metadata['accountCount'] ?? '0');
      return n > 0 ? `${n} account${n === 1 ? '' : 's'}` : null;
    }
    if (integration.provider === 'azure') {
      const n = Number(integration.metadata['subscriptionCount'] ?? '0');
      return n > 0 ? `${n} subscription${n === 1 ? '' : 's'}` : null;
    }
    return null;
  }

  get connectedIntegrationCount(): number {
    return this.context?.integrations.filter(i => i.status === 'connected').length ?? 0;
  }

  // ── AWS multi-account helpers ─────────────────────────────────────────────────

  get awsAccounts(): AdminAwsAccount[] {
    // Primary first, then by accountId, so the primary is always obvious.
    return [...(this.context?.awsAccounts ?? [])].sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.accountId.localeCompare(b.accountId);
    });
  }

  get awsSummary(): AdminAwsSummary | null {
    return this.context?.awsSummary ?? null;
  }

  /** 'none' (no AWS), 'degraded' (accounts but not all healthy/active), or 'healthy'. */
  get awsOverallState(): 'none' | 'healthy' | 'degraded' {
    const s = this.awsSummary;
    if (!s || !s.connected) return 'none';
    return s.degraded || !s.hasPrimary ? 'degraded' : 'healthy';
  }

  awsAccountLabel(a: AdminAwsAccount): string {
    return a.label && a.label !== a.accountId ? `${a.label} · ${a.accountId}` : a.accountId;
  }

  awsStatusBadgeClass(status: string): string {
    const map: Record<string, string> = {
      ACTIVE:               'bg-emerald-100 text-emerald-700',
      FAILED:               'bg-red-100 text-red-700',
      PENDING_VERIFICATION: 'bg-amber-100 text-amber-700',
    };
    return map[status] ?? 'bg-zinc-100 text-zinc-600';
  }

  awsStatusLabel(status: string): string {
    const map: Record<string, string> = {
      ACTIVE:               'Active',
      FAILED:               'Failed',
      PENDING_VERIFICATION: 'Pending',
    };
    return map[status] ?? status;
  }

  awsHealthDotClass(health: string): string {
    const map: Record<string, string> = {
      OK:      'bg-emerald-500',
      STALE:   'bg-amber-400',
      ERROR:   'bg-red-500',
      UNKNOWN: 'bg-zinc-300',
    };
    return map[health] ?? 'bg-zinc-300';
  }

  awsHealthLabel(health: string): string {
    const map: Record<string, string> = {
      OK: 'Healthy', STALE: 'Stale', ERROR: 'Error', UNKNOWN: 'Unknown',
    };
    return map[health] ?? health;
  }

  /** Per-account region summary: "us-east-1 (default) +2". */
  awsRegionSummary(a: AdminAwsAccount): string {
    if (!a.enabledRegions?.length) return a.defaultRegion || '—';
    const extra = a.enabledRegions.length - 1;
    const base  = a.defaultRegion || a.enabledRegions[0];
    return extra > 0 ? `${base} (default) +${extra}` : `${base} (default)`;
  }

  // ── Azure multi-subscription helpers (read-only in v1) ────────────────────────
  // Status/health badge classes are provider-agnostic, so the AWS ones are reused.

  get azureSubscriptions(): AdminAzureSubscription[] {
    return [...(this.context?.azureSubscriptions ?? [])].sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.subscriptionId.localeCompare(b.subscriptionId);
    });
  }

  get azureSummary(): AdminAzureSummary | null {
    return this.context?.azureSummary ?? null;
  }

  get azureOverallState(): 'none' | 'healthy' | 'degraded' {
    const s = this.azureSummary;
    if (!s || !s.connected) return 'none';
    return s.degraded || !s.hasPrimary ? 'degraded' : 'healthy';
  }

  azureSubscriptionLabel(s: AdminAzureSubscription): string {
    return s.label && s.label !== s.subscriptionId ? `${s.label} · ${s.subscriptionId}` : s.subscriptionId;
  }

  /** Per-subscription location summary: "eastus (default) +2". */
  azureLocationSummary(s: AdminAzureSubscription): string {
    if (!s.enabledLocations?.length) return s.defaultLocation || '—';
    const extra = s.enabledLocations.length - 1;
    const base  = s.defaultLocation || s.enabledLocations[0];
    return extra > 0 ? `${base} (default) +${extra}` : `${base} (default)`;
  }

  // ── Azure subscription actions (support-initiated) ────────────────────────
  // Mirror the AWS account actions, using the same shared management pattern.

  /** Re-verify a subscription against Azure using its stored service principal. */
  verifyAzureSubscription(s: AdminAzureSubscription): void {
    const orgId = this.orgIdForActions;
    if (!orgId || this.azureActionSubId) return;
    this.azureActionSubId = s.subscriptionId;
    this.azureActionError = null;
    this.azureActionResult = null;
    this.adminService.adminVerifyAzureSubscription(orgId, s.subscriptionId).subscribe({
      next: res => {
        this.azureActionSubId = null;
        this.azureActionResult = res.status === 'ACTIVE'
          ? `Subscription ${s.subscriptionId} re-verified — Active.`
          : `Subscription ${s.subscriptionId} verification failed: ${res.failureReason ?? 'unknown error'}.`;
        this.silentRefreshContext();
        this.loadAzureEvents(orgId);
      },
      error: (err: { error?: { message?: string } }) => {
        this.azureActionSubId = null;
        this.azureActionError = err?.error?.message ?? 'Verification request failed.';
      },
    });
  }

  askDisconnectAzureSubscription(s: AdminAzureSubscription): void {
    this.azureConfirmDisconnect = s;
    this.azureActionError = null;
  }

  cancelDisconnectAzureSubscription(): void {
    this.azureConfirmDisconnect = null;
  }

  confirmDisconnectAzureSubscription(): void {
    const orgId = this.orgIdForActions;
    const s = this.azureConfirmDisconnect;
    if (!orgId || !s || this.azureActionSubId) return;
    this.azureActionSubId = s.subscriptionId;
    this.azureActionError = null;
    this.adminService.adminDisconnectAzureSubscription(orgId, s.subscriptionId).subscribe({
      next: res => {
        this.azureActionSubId = null;
        this.azureConfirmDisconnect = null;
        const promoted = res.promotedSubscriptionId ? ` Promoted ${res.promotedSubscriptionId} to primary.` : '';
        this.azureActionResult = `Subscription ${s.subscriptionId} disconnected — freed ${res.cleanup.quotaFreed} monitored slot(s).${promoted}`;
        this.silentRefreshContext();
        this.loadAzureEvents(orgId);
      },
      error: (err: { error?: { message?: string } }) => {
        this.azureActionSubId = null;
        this.azureConfirmDisconnect = null;
        this.azureActionError = err?.error?.message ?? 'Disconnect request failed.';
      },
    });
  }

  loadAzureEvents(orgId: string): void {
    this.azureEventsLoading = true;
    this.adminService.adminGetAzureEvents(orgId, 7).subscribe({
      next: res => { this.azureEvents = res.events; this.azureEventsLoading = false; },
      error: () => { this.azureEventsLoading = false; },
    });
  }

  azureEventLabel(eventType: string): string {
    const map: Record<string, string> = {
      azure_subscription_verified:          'Verified',
      azure_subscription_disconnected:      'Disconnected',
      azure_primary_promoted:               'Primary promoted',
      azure_subscription_locations_updated: 'Locations updated',
    };
    return map[eventType] ?? eventType;
  }

  // ── GCP projects (support-initiated) — parallel to AWS/Azure ──────────────────

  get gcpProjects(): AdminGcpProject[] {
    return [...(this.context?.gcpProjects ?? [])].sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.projectId.localeCompare(b.projectId);
    });
  }

  get gcpSummary(): AdminGcpSummary | null {
    return this.context?.gcpSummary ?? null;
  }

  get gcpOverallState(): 'none' | 'healthy' | 'degraded' {
    const s = this.gcpSummary;
    if (!s || !s.connected) return 'none';
    return s.degraded || !s.hasPrimary ? 'degraded' : 'healthy';
  }

  gcpProjectLabel(p: AdminGcpProject): string {
    return p.label && p.label !== p.projectId ? `${p.label} · ${p.projectId}` : p.projectId;
  }

  /** Per-project region summary: "us-central1 (default) +2". */
  gcpRegionSummary(p: AdminGcpProject): string {
    if (!p.enabledRegions?.length) return p.defaultRegion || '—';
    const extra = p.enabledRegions.length - 1;
    const base  = p.defaultRegion || p.enabledRegions[0];
    return extra > 0 ? `${base} (default) +${extra}` : `${base} (default)`;
  }

  /** Re-verify a project against GCP using its stored service account. */
  verifyGcpProject(p: AdminGcpProject): void {
    const orgId = this.orgIdForActions;
    if (!orgId || this.gcpActionProjectId) return;
    this.gcpActionProjectId = p.projectId;
    this.gcpActionError = null;
    this.gcpActionResult = null;
    this.adminService.adminVerifyGcpProject(orgId, p.projectId).subscribe({
      next: res => {
        this.gcpActionProjectId = null;
        this.gcpActionResult = res.status === 'ACTIVE'
          ? `Project ${p.projectId} re-verified — Active.`
          : `Project ${p.projectId} verification failed: ${res.failureReason ?? 'unknown error'}.`;
        this.silentRefreshContext();
        this.loadGcpEvents(orgId);
      },
      error: (err: { error?: { message?: string } }) => {
        this.gcpActionProjectId = null;
        this.gcpActionError = err?.error?.message ?? 'Verification request failed.';
      },
    });
  }

  askDisconnectGcpProject(p: AdminGcpProject): void {
    this.gcpConfirmDisconnect = p;
    this.gcpActionError = null;
  }

  cancelDisconnectGcpProject(): void {
    this.gcpConfirmDisconnect = null;
  }

  confirmDisconnectGcpProject(): void {
    const orgId = this.orgIdForActions;
    const p = this.gcpConfirmDisconnect;
    if (!orgId || !p || this.gcpActionProjectId) return;
    this.gcpActionProjectId = p.projectId;
    this.gcpActionError = null;
    this.adminService.adminDisconnectGcpProject(orgId, p.projectId).subscribe({
      next: res => {
        this.gcpActionProjectId = null;
        this.gcpConfirmDisconnect = null;
        const promoted = res.promotedProjectId ? ` Promoted ${res.promotedProjectId} to primary.` : '';
        this.gcpActionResult = `Project ${p.projectId} disconnected — freed ${res.cleanup.quotaFreed} monitored slot(s).${promoted}`;
        this.silentRefreshContext();
        this.loadGcpEvents(orgId);
      },
      error: (err: { error?: { message?: string } }) => {
        this.gcpActionProjectId = null;
        this.gcpConfirmDisconnect = null;
        this.gcpActionError = err?.error?.message ?? 'Disconnect request failed.';
      },
    });
  }

  loadGcpEvents(orgId: string): void {
    this.gcpEventsLoading = true;
    this.adminService.adminGetGcpEvents(orgId, 7).subscribe({
      next: res => { this.gcpEvents = res.events; this.gcpEventsLoading = false; },
      error: () => { this.gcpEventsLoading = false; },
    });
  }

  gcpEventLabel(eventType: string): string {
    const map: Record<string, string> = {
      gcp_project_verified:        'Verified',
      gcp_project_disconnected:    'Disconnected',
      gcp_primary_promoted:        'Primary promoted',
      gcp_project_regions_updated: 'Regions updated',
    };
    return map[eventType] ?? eventType;
  }

  // ── AWS account actions ───────────────────────────────────────────────────

  private get orgIdForActions(): string | null {
    return this.context?.organization.orgId ?? this._orgId;
  }

  /** Re-verify an account by re-assuming its stored role (server-side). */
  verifyAwsAccount(a: AdminAwsAccount): void {
    const orgId = this.orgIdForActions;
    if (!orgId || this.awsActionAccountId) return;
    this.awsActionAccountId = a.accountId;
    this.awsActionError = null;
    this.awsActionResult = null;
    this.adminService.adminVerifyAwsAccount(orgId, a.accountId).subscribe({
      next: res => {
        this.awsActionAccountId = null;
        this.awsActionResult = res.status === 'ACTIVE'
          ? `Account ${a.accountId} re-verified — Active.`
          : `Account ${a.accountId} verification failed: ${res.failureReason ?? 'unknown error'}.`;
        this.silentRefreshContext();
        this.loadAwsEvents(orgId);
      },
      error: (err: { error?: { message?: string } }) => {
        this.awsActionAccountId = null;
        this.awsActionError = err?.error?.message ?? 'Verification request failed.';
      },
    });
  }

  askDisconnectAwsAccount(a: AdminAwsAccount): void {
    this.awsConfirmDisconnect = a;
    this.awsActionError = null;
  }

  cancelDisconnectAwsAccount(): void {
    this.awsConfirmDisconnect = null;
  }

  confirmDisconnectAwsAccount(): void {
    const orgId = this.orgIdForActions;
    const a = this.awsConfirmDisconnect;
    if (!orgId || !a || this.awsActionAccountId) return;
    this.awsActionAccountId = a.accountId;
    this.awsActionError = null;
    this.adminService.adminDisconnectAwsAccount(orgId, a.accountId).subscribe({
      next: res => {
        this.awsActionAccountId = null;
        this.awsConfirmDisconnect = null;
        const promoted = res.promotedAccountId ? ` Promoted ${res.promotedAccountId} to primary.` : '';
        this.awsActionResult = `Account ${a.accountId} disconnected — freed ${res.cleanup.quotaFreed} monitored slot(s).${promoted}`;
        this.silentRefreshContext();
        this.loadAwsEvents(orgId);
      },
      error: (err: { error?: { message?: string } }) => {
        this.awsActionAccountId = null;
        this.awsConfirmDisconnect = null;
        this.awsActionError = err?.error?.message ?? 'Disconnect request failed.';
      },
    });
  }

  startEditAwsRegions(a: AdminAwsAccount): void {
    this.awsEditRegionsAccountId = a.accountId;
    this.awsAcctEnabledRegions = a.enabledRegions?.length ? [...a.enabledRegions] : ['us-east-1'];
    this.awsAcctDefaultRegion  = a.defaultRegion ?? this.awsAcctEnabledRegions[0];
    this.awsAcctRegionsError = null;
  }

  cancelEditAwsRegions(): void {
    this.awsEditRegionsAccountId = null;
    this.awsAcctRegionsError = null;
  }

  toggleAwsRegion(code: string): void {
    if (this.awsAcctEnabledRegions.includes(code)) {
      if (code === this.awsAcctDefaultRegion) return;
      this.awsAcctEnabledRegions = this.awsAcctEnabledRegions.filter(r => r !== code);
    } else {
      this.awsAcctEnabledRegions = [...this.awsAcctEnabledRegions, code];
    }
    this.awsAcctRegionsError = null;
  }

  isAwsRegionEnabled(code: string): boolean {
    return this.awsAcctEnabledRegions.includes(code);
  }

  saveAwsRegions(a: AdminAwsAccount): void {
    const orgId = this.orgIdForActions;
    if (!orgId) return;
    if (this.awsAcctEnabledRegions.length === 0) { this.awsAcctRegionsError = 'Enable at least one region.'; return; }
    if (!this.awsAcctEnabledRegions.includes(this.awsAcctDefaultRegion)) { this.awsAcctRegionsError = 'The default region must be enabled.'; return; }
    this.awsActionAccountId = a.accountId;
    this.awsAcctRegionsError = null;
    this.adminService.adminUpdateAwsAccountRegions(orgId, a.accountId, this.awsAcctDefaultRegion, this.awsAcctEnabledRegions).subscribe({
      next: () => {
        this.awsActionAccountId = null;
        this.awsEditRegionsAccountId = null;
        this.awsActionResult = `Regions updated for ${a.accountId}.`;
        this.silentRefreshContext();
        this.loadAwsEvents(orgId);
      },
      error: (err: { error?: { message?: string } }) => {
        this.awsActionAccountId = null;
        this.awsAcctRegionsError = err?.error?.message ?? 'Failed to update regions.';
      },
    });
  }

  loadAwsEvents(orgId: string): void {
    this.awsEventsLoading = true;
    this.adminService.adminGetAwsEvents(orgId, 7).subscribe({
      next: res => { this.awsEvents = res.events; this.awsEventsLoading = false; },
      error: () => { this.awsEventsLoading = false; },
    });
  }

  awsEventLabel(eventType: string): string {
    const map: Record<string, string> = {
      aws_account_verified:       'Verified',
      aws_account_disconnected:   'Disconnected',
      aws_primary_promoted:       'Primary promoted',
      aws_account_regions_updated: 'Regions updated',
    };
    return map[eventType] ?? eventType;
  }

  awsEventDotClass(status: string): string {
    return status === 'FAILURE' ? 'bg-red-500' : 'bg-emerald-500';
  }

  // ── Account-grouped diagnostics ───────────────────────────────────────────

  /**
   * Monitored services grouped by cloud scope (AWS account or Azure subscription),
   * with provider-native labels. A group's provider is derived from its services'
   * serviceType, so the scope noun ("account" vs "subscription") and the primary
   * marker adapt correctly for each cloud.
   */
  get monitoredServicesByScope(): { scopeId: string; label: string; scopeNoun: string; services: AdminMonitoredService[] }[] {
    const svcs = this.context?.monitoredServices ?? [];
    if (svcs.length === 0) return [];
    const awsPrimary   = this.awsSummary?.primaryAccountId ?? null;
    const azurePrimary = this.azureSummary?.primarySubscriptionId ?? null;
    const gcpPrimary   = this.gcpSummary?.primaryProjectId ?? null;
    const isPrimaryScope = (id: string): boolean => id === awsPrimary || id === azurePrimary || id === gcpPrimary;
    const groups = new Map<string, AdminMonitoredService[]>();
    for (const s of svcs) {
      const key = s.accountId ?? awsPrimary ?? 'primary';
      (groups.get(key) ?? groups.set(key, []).get(key)!).push(s);
    }
    return [...groups.entries()]
      .map(([scopeId, services]) => {
        const provider  = providerForServiceType(services[0]?.serviceType ?? '');
        const scopeNoun = providerMeta(provider).scopeNoun;   // 'account' | 'subscription' | 'project'
        const primaryId = provider === 'AZURE' ? azurePrimary : provider === 'GCP' ? gcpPrimary : awsPrimary;
        const label = scopeId === 'primary'
          ? `Primary ${scopeNoun}`
          : (scopeId === primaryId ? `${scopeId} (primary)` : scopeId);
        return { scopeId, label, scopeNoun, services };
      })
      .sort((a, b) => (isPrimaryScope(a.scopeId) ? -1 : isPrimaryScope(b.scopeId) ? 1 : a.scopeId.localeCompare(b.scopeId)));
  }

  selectTab(tab: 'overview' | 'team' | 'services' | 'monitoring'): void {
    this.activeTab = tab;
    if (tab === 'monitoring' && !this.monitoringContext && !this.monitoringLoading && this._orgId) {
      this.loadMonitoring(this._orgId);
    }
  }

  retryMonitoring(): void {
    if (this._orgId) this.loadMonitoring(this._orgId);
  }

  applyFindingFilters(): void {
    if (this._orgId) this.loadFindings(this._orgId);
  }

  private loadMonitoring(orgId: string): void {
    this.monitoringSub?.unsubscribe();
    this.monitoringLoading = true;
    this.monitoringError   = null;
    this.monitoringContext = null;
    this.monitoringSub = this.adminService.getOrgMonitoring(orgId).subscribe({
      next: data => {
        this.monitoringContext = data;
        this.monitoringLoading = false;
        this.loadFindings(orgId);
      },
      error: () => {
        this.monitoringError   = 'Failed to load monitoring data.';
        this.monitoringLoading = false;
      },
    });
  }

  private loadFindings(orgId: string): void {
    this.findingsSub?.unsubscribe();
    this.findingsLoading = true;
    this.findingsError   = null;
    this.findingsSub = this.adminService.getOrgMonitoringFindings(orgId, {
      limit:    50,
      severity: this.findingSeverityFilter || undefined,
      state:    this.findingStateFilter    || undefined,
    }).subscribe({
      next: res => {
        this.monitoringFindings = res.findings;
        this.findingsLoading    = false;
      },
      error: () => {
        this.findingsError   = 'Failed to load findings.';
        this.findingsLoading = false;
      },
    });
  }

  // ── Team member helpers ───────────────────────────────────────────────────────

  memberInitials(member: AdminTeamMember): string {
    return ((member.givenName?.[0] ?? '') + (member.familyName?.[0] ?? '')).toUpperCase() || '?';
  }

  memberRoleLabel(role: string): string {
    return ({ owner: 'Owner', admin: 'Admin', member: 'Member' } as Record<string, string>)[role] ?? role;
  }

  memberRoleBadgeClass(role: string): string {
    const map: Record<string, string> = {
      owner:  'bg-violet-100 text-violet-700',
      admin:  'bg-amber-100 text-amber-700',
      member: 'bg-zinc-100 text-zinc-600',
    };
    return map[role] ?? 'bg-zinc-100 text-zinc-600';
  }

  // ── Monitored service helpers ─────────────────────────────────────────────────

  // Service-type label/badge/icon come from the SHARED admin.service maps (AWS +
  // all 20 Azure services); unknown types fall back gracefully. These thin
  // delegates keep the template stable while the source of truth stays shared.
  serviceTypeLabel(type: string): string { return humanizeServiceType(type); }
  serviceTypeBadgeClass(type: string): string { return serviceTypeBadgeClass(type); }
  serviceTypeIcon(type: string): string { return serviceTypeIcon(type); }
  isGlobalServiceType(type: string): boolean { return isGlobalServiceType(type); }

  serviceDisplayName(svc: AdminMonitoredService): string {
    return svc.name !== svc.resourceId ? svc.name : svc.resourceId;
  }

  // ── Monitoring tab helpers ─────────────────────────────────────────────────

  healthBadgeClass(health: string): string {
    const map: Record<string, string> = {
      HEALTHY:        'bg-emerald-100 text-emerald-700',
      UNHEALTHY:      'bg-red-100 text-red-700',
      UNKNOWN:        'bg-amber-100 text-amber-700',
      NOT_CONFIGURED: 'bg-zinc-100 text-zinc-500',
    };
    return map[health] ?? 'bg-zinc-100 text-zinc-500';
  }

  healthLabel(health: string): string {
    const map: Record<string, string> = {
      HEALTHY:        'Healthy',
      UNHEALTHY:      'Unhealthy',
      UNKNOWN:        'Unknown',
      NOT_CONFIGURED: 'Not Configured',
    };
    return map[health] ?? health;
  }

  healthDotClass(health: string): string {
    const map: Record<string, string> = {
      HEALTHY:        'bg-emerald-500',
      UNHEALTHY:      'bg-red-500',
      UNKNOWN:        'bg-amber-400',
      NOT_CONFIGURED: 'bg-zinc-300',
    };
    return map[health] ?? 'bg-zinc-300';
  }

  severityBadgeClass(severity: string): string {
    const map: Record<string, string> = {
      CRITICAL: 'bg-red-100 text-red-800',
      HIGH:     'bg-orange-100 text-orange-700',
      MEDIUM:   'bg-amber-100 text-amber-700',
      LOW:      'bg-sky-100 text-sky-700',
      INFO:     'bg-zinc-100 text-zinc-600',
    };
    return map[severity] ?? 'bg-zinc-100 text-zinc-600';
  }

  findingStateClass(state: string): string {
    return state === 'OPEN' ? 'text-red-600' : 'text-zinc-400';
  }

  findingStateLabel(state: string): string {
    return state === 'OPEN' ? 'Open' : 'Resolved';
  }

  sourceLabel(source: string): string {
    const map: Record<string, string> = {
      AUTOMATED: 'Automated',
      MANUAL:    'Manual',
    };
    return map[source] ?? source;
  }

  monitoringModeLabel(mode: string | null): string {
    if (!mode) return '—';
    const map: Record<string, string> = {
      ACTIVE:  'Active',
      PASSIVE: 'Passive',
    };
    return map[mode] ?? mode;
  }

  get openFindingCount(): number {
    return this.monitoringContext?.summary.degradedCount ?? 0;
  }

  formatRelativeTime(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    try {
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins  = Math.floor(diff / 60000);
      if (mins < 1)    return 'just now';
      if (mins < 60)   return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs  < 24)   return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      return `${days}d ago`;
    } catch {
      return '—';
    }
  }

  monitoringServiceIcon(svc: AdminMonitoringServiceState): string {
    return this.serviceTypeIcon(svc.serviceType);
  }
}
